"use client"
import { useQualityCopy } from "@/components/i18n/quality-copy-context"
import { useQualityLearning } from "@/hooks/use-quality-presentations"

import { useQualityChanges } from "@/hooks/use-quality-presentations"


import { useCallback, useId, useState } from "react"

import { Button } from "@/components/ui/button"
import { DiffLines } from "@/components/ui/prompt-diff"
import { useApplySuggestionMutation, useRejectSuggestionMutation } from "@/hooks/mutations/conversation-quality"
import { usePermissions } from "@/hooks/use-permissions"
import { MESSAGING_AGENTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { messagingAgentHref } from "@/lib/agents/messaging/page-link"
import { type WaitingDescription } from "@/lib/conversation-quality/change-display"
import { questionWithKind } from "@/lib/conversation-quality/outcome-display"
import { type InstructionsRoom } from "@/lib/conversation-quality/self-learning-display"
import type { PromptEditPayload } from "@/lib/conversation-quality/suggestions/schema"
import { cn } from "@/lib/utils"
import type { QualitySuggestionsResult } from "@/server-actions/conversation-quality"

import { ArticleReviewDialog } from "../article-review-dialog"
import { FindImprovementsNow } from "../find-improvements-now"
import { InstructionReviewDialog } from "../instruction-review-dialog"
import {
  LANDING_SCROLL_MARGIN,
  revealSuggestion,
  SectionCard,
  SessionLinks,
  suggestionElementId,
  useLandedScroll,
} from "./section-parts"
import { useTranslations } from "next-intl"



type Suggestion = QualitySuggestionsResult["suggestions"][number]

/**
 * The drafts that wait for a person, newest first, and «Find improvements now», which keeps the section while none
 * waits. A queue: it does not follow the period. The review dialogs open from here, for an article, a long line, and
 * whatever an apply from the list cannot settle.
 */
export function NeedsDecision({
  data,
  agentId,
  timezone,
  landing,
  onLanded,
}: {
  data: QualitySuggestionsResult | undefined;
  /** `null` is «All agents», where each item names its agent. */
  agentId: string | null;
  /** The viewer's, for the closing dates. */
  timezone: string;
  /** A waiting change a link opened the tab on: brought into view once listed, else the card. */
  landing: string | null;
  onLanded: () => void;
}) {
  const { waitingCount, waitingListed } = useQualityChanges()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsDecisionSections")

  const { can } = usePermissions()
  const [reviewingId, setReviewingId] = useState<string | null>(null)
  // Read from the current list, so a draft decided elsewhere closes with its item.
  const reviewing = data?.suggestions.find((suggestion) => suggestion.id === reviewingId)
  // A closed review's id is let go, or it would read as open and keep the next conflict's review shut.
  if (reviewingId !== null && data && !reviewing) setReviewingId(null)
  const revealLanding = useCallback(() => {
    if (!landing || !data?.suggestions.some((suggestion) => suggestion.id === landing)) return false
    revealSuggestion(landing)
    return true
  }, [landing, data])
  const ref = useLandedScroll<HTMLDivElement>(landing !== null, data !== undefined, onLanded, revealLanding)

  if (!data) return null
  const listed = data.suggestions.length > 0

  return (
    <SectionCard
      ref={ref}
      className={LANDING_SCROLL_MARGIN}
      title={tUi("needsYourDecision")}
      subtitle={tUi("changesTheAgentWonTMakeWithoutYour")}
      aside={data.waiting > 0 && <span className="text-xs text-muted-foreground">{waitingCount(data.waiting)}</span>}
    >
      {listed && (
        <ul className="-my-3 divide-y">
          {data.suggestions.map((suggestion) => (
            <DecisionItem
              key={suggestion.id}
              suggestion={suggestion}
              showAgent={agentId === null}
              timezone={timezone}
              landed={landing === suggestion.id}
              canDecide={can(MESSAGING_AGENTS_PERMISSIONS.MANAGE)}
              onReview={() => setReviewingId(suggestion.id)}
              // An apply that meets a conflict opens its review, but never over one the person is working in.
              onConflict={() => setReviewingId((open) => open ?? suggestion.id)}
            />
          ))}
        </ul>
      )}
      <div className={cn("space-y-3", listed && "mt-4 border-t pt-3")}>
        {data.waiting > data.suggestions.length && (
          <p className="text-xs text-muted-foreground">{waitingListed(data.suggestions.length)}</p>
        )}
        {/* Per scope: a run seen in one is not another's. */}
        <FindImprovementsNow key={agentId ?? "all"} agentId={agentId} timezone={timezone} />
      </div>
      {reviewing && (
        <ReviewDialog key={reviewing.id} suggestion={reviewing} onClose={() => setReviewingId(null)} />
      )}
    </SectionCard>
  )
}

function DecisionItem({
  suggestion,
  showAgent,
  timezone,
  landed,
  canDecide,
  onReview,
  onConflict,
}: {
  suggestion: Suggestion;
  showAgent: boolean;
  timezone: string;
  /** A link opened the tab on this draft: its details are what the person came for. */
  landed: boolean;
  canDecide: boolean;
  onReview: () => void;
  onConflict: () => void;
}) {
  const { TOPIC_CATEGORY_LABELS } = useQualityCopy()
  const { describeWaiting, LEARNED_FROM_TEAM, draftMetaLine } = useQualityChanges()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsDecisionSections")

  const [open, setOpen] = useState(landed)
  const detailsId = useId()
  const edit = suggestion.kind === "prompt_edit" ? suggestion.edit : null
  const noRoom = suggestion.kind === "prompt_edit" ? suggestion.noRoom : null
  const waiting = describeWaiting({
    kind: suggestion.kind,
    operation: suggestion.kind === "prompt_edit" ? suggestion.operation : null,
    reason: suggestion.reason,
    edit,
    checkReason: suggestion.kind === "prompt_edit" ? suggestion.checkReason : null,
    replacing: suggestion.kind === "kb_addition" ? (suggestion.before?.author ?? null) : null,
  })
  const about = [
    ...(showAgent ? [suggestion.agentName] : []),
    tUi("question", {question: questionWithKind(suggestion.topicLabel, suggestion.topicCategory, TOPIC_CATEGORY_LABELS)}),
    ...(suggestion.teamOnly ? [LEARNED_FROM_TEAM] : []),
  ].join(" · ")

  return (
    <li id={suggestionElementId(suggestion.id)} className="py-3">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-72 space-y-1">
          <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">{waiting.headline}</p>
          <p className="text-[15px] font-semibold leading-snug [text-wrap:pretty]">{suggestion.title}</p>
          <p className="text-sm text-muted-foreground [text-wrap:pretty]">{about}</p>
          <p className="text-xs text-muted-foreground">{draftMetaLine(suggestion, timezone)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen(!open)}>
            <span key={open ? tUi("hideDetails") : tUi("details")}>{open ? tUi("hideDetails") : tUi("details")}</span>
          </Button>
          {canDecide && (
            <Decisions
              suggestion={suggestion}
              edit={edit}
              waiting={waiting}
              blocked={noRoom !== null}
              onReview={onReview}
              onConflict={onConflict}
            />
          )}
        </div>
      </div>
      <div className="mt-2 space-y-1.5 empty:hidden">
        {noRoom && <NoRoomNotice room={noRoom} agentId={suggestion.agentId} />}
        {waiting.found && <p className="text-sm leading-snug [text-wrap:pretty]">{waiting.found}</p>}
        {waiting.note && <p className="text-xs text-amber-700 dark:text-amber-400">{waiting.note}</p>}
        {waiting.link && (
          <Button asChild variant="link" size="sm" className="h-auto px-0 text-xs">
            <a href={waiting.link.href}>{waiting.link.label}</a>
          </Button>
        )}
        {suggestion.kind === "prompt_edit" && suggestion.instructionsChanged && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            <span key={tUi("theAgentSInstructionsChangedAfterThisWas")}>{tUi("theAgentSInstructionsChangedAfterThisWas")}</span></p>
        )}
      </div>
      {open && (
        <div id={detailsId} className="mt-3 space-y-3 rounded-lg border bg-muted/30 p-3 sm:p-4">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground"><span key={tUi("why")}>{tUi("why")}</span></p>
            <p className="text-sm leading-relaxed [text-wrap:pretty]">{suggestion.rationale}</p>
          </div>
          <ChangePreview suggestion={suggestion} />
          {suggestion.evidence && <SessionLinks label={tUi("examples")} sessions={suggestion.evidence} />}
        </div>
      )}
    </li>
  )
}

/** Applying it would grow the instructions past their limit: the person shortens them first. */
function NoRoomNotice({ room, agentId }: { room: InstructionsRoom; agentId: string }) {
  const { instructionsRoomLabel } = useQualityLearning()


  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsDecisionSections")

  return (
    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
      <span>{<span key={tUi("doesnTFitTheInstructionsAreValue0", { value0: String(instructionsRoomLabel(room)) })}>{tUi("doesnTFitTheInstructionsAreValue0", { value0: String(instructionsRoomLabel(room)) })}</span>}</span>
      <a href={messagingAgentHref(agentId)} className="font-semibold underline underline-offset-2">
        <span key={tUi("shortenThem")}>{tUi("shortenThem")}</span></a>
      <span> <span key={tUi("andThisCanBeApplied")}>{tUi("andThisCanBeApplied")}</span></span>
    </p>
  )
}

/** What applying it writes: the line it removes and the one it adds, what an article replaces, or its opening. */
function ChangePreview({ suggestion }: { suggestion: Suggestion }) {
  const { articleLines, changeLines } = useQualityChanges()

  switch (suggestion.kind) {
    case "kb_addition":
      return suggestion.before ? (
        <div className="rounded-md border bg-background p-2.5 text-sm">
          <DiffLines lines={articleLines(suggestion.before, suggestion)} />
        </div>
      ) : (
        <div className="space-y-1 rounded-md border bg-background p-2.5 text-sm">
          <p className="font-medium">{suggestion.title}</p>
          <p className="whitespace-pre-wrap break-words text-muted-foreground">{suggestion.content}</p>
        </div>
      )
    case "prompt_edit":
      return suggestion.edit ? (
        <div className="rounded-md border bg-background p-2.5 text-sm">
          <DiffLines lines={changeLines(suggestion.edit)} />
        </div>
      ) : null
    default: {
      const exhaustive: never = suggestion
      return exhaustive
    }
  }
}

/** The person's no, and the apply — on the spot, or through the review when the change needs one. */
function Decisions({
  suggestion,
  edit,
  waiting,
  blocked,
  onReview,
  onConflict,
}: {
  suggestion: Suggestion;
  edit: PromptEditPayload | null;
  waiting: WaitingDescription;
  /** Would not fit the instructions: declined, never applied. */
  blocked: boolean;
  onReview: () => void;
  onConflict: () => void;
}) {
  const apply = useApplySuggestionMutation()
  const reject = useRejectSuggestionMutation(suggestion.kind)
  const deciding = apply.isPending || reject.isPending
  // An instruction change withheld from the caller can be declined but not shown, so not applied.
  const reviewable = suggestion.kind === "kb_addition" || edit !== null

  const applyInline = (change: PromptEditPayload) => {
    apply
      .mutateAsync({
        suggestionId: suggestion.id,
        instruction: change.operation === "remove" ? "" : change.instruction,
      })
      .then((outcome) => {
        if (outcome === "review") onConflict()
      })
      .catch(() => {})
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={deciding}
        onClick={() => reject.mutate({ input: { suggestionId: suggestion.id } })}
      >
        <span>{waiting.decline}</span>
      </Button>
      {reviewable && (
        <ApplyButton
          mode={waiting.apply}
          disabled={deciding || blocked}
          onApply={() => (edit ? applyInline(edit) : onReview())}
          onReview={onReview}
        />
      )}
    </>
  )
}

function ApplyButton({
  mode,
  disabled,
  onApply,
  onReview,
}: {
  mode: WaitingDescription["apply"];
  disabled: boolean;
  onApply: () => void;
  onReview: () => void;
}) {
  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsDecisionSections")

  switch (mode) {
    case "inline":
      return (
        <Button size="sm" disabled={disabled} onClick={onApply}>
          <span><span key={tUi("apply")}>{tUi("apply")}</span></span>
        </Button>
      )
    case "review":
      return (
        <Button size="sm" disabled={disabled} onClick={onReview}>
          <span><span key={tUi("review")}>{tUi("review")}</span></span>
        </Button>
      )
    default: {
      const exhaustive: never = mode
      return exhaustive
    }
  }
}

function ReviewDialog({ suggestion, onClose }: { suggestion: Suggestion; onClose: () => void }) {
  const onOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  switch (suggestion.kind) {
    case "kb_addition":
      return <ArticleReviewDialog suggestion={suggestion} open onOpenChange={onOpenChange} />
    case "prompt_edit":
      return suggestion.edit ? (
        <InstructionReviewDialog suggestion={suggestion} edit={suggestion.edit} open onOpenChange={onOpenChange} />
      ) : null
    default: {
      const exhaustive: never = suggestion
      return exhaustive
    }
  }
}

